import type { CurrentUser, Membership } from '@mfos/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useMatch, useNavigate } from 'react-router';
import { ErrorNotice, LoadingNotice } from '../components/ErrorNotice';
import { LanguageSwitcher } from '../lib/i18n/context';
import { phrase, useI18n } from '../lib/i18n/runtime';
import { isApiError } from '../lib/api-client';
import { loginPathFor } from '../lib/safe-next';
import { useCurrentUser, useLogout } from '../features/auth/auth.api';
import { hasAdminAccess } from '../features/admin/admin-tabs';
import { NotificationBell } from '../features/notifications/NotificationBell';
import { HeaderSearch } from '../features/search/HeaderSearch';
import { can } from '../features/workspace/membership-context';
import { useMemberships } from '../features/workspace/workspace.api';

/** Gate for every signed-in page: resolves the session (refreshing it once) or sends to /login. */
export function AuthenticatedLayout() {
  const location = useLocation();
  const me = useCurrentUser();
  const { t } = useI18n();

  if (me.isPending) {
    return (
      <main id="main" className="app-main" tabIndex={-1}>
        <LoadingNotice>{t('loadingSession')}</LoadingNotice>
      </main>
    );
  }
  if (me.isError) {
    if (isApiError(me.error, 'UNAUTHENTICATED')) {
      return <Navigate to={loginPathFor(`${location.pathname}${location.search}`)} replace />;
    }
    return (
      <main id="main" className="app-main stack" tabIndex={-1}>
        <h1>{phrase('We could not load your session')}</h1>
        <ErrorNotice error={me.error} />
        <button type="button" className="button" onClick={() => void me.refetch()}>
          {phrase('Try again')}
        </button>
      </main>
    );
  }

  return <SignedInShell user={me.data} />;
}

function SignedInShell({ user }: { user: CurrentUser }) {
  const { t } = useI18n();
  const location = useLocation();
  const memberships = useMemberships();
  const match = useMatch({ path: '/w/:workspaceId', end: false });
  // The small-screen menu is open only for the page it was opened on, so following a link closes it.
  const [navOpenAt, setNavOpenAt] = useState<string | null>(null);
  const navOpen = navOpenAt === location.pathname;
  const items = memberships.data?.items ?? [];
  const currentId = match?.params.workspaceId;
  const current =
    items.find((item) => item.workspace.id === currentId) ??
    (currentId === undefined ? items[0] : undefined);

  return (
    <>
      <a className="skip-link" href="#main">
        {t('skip')}
      </a>
      <header className="app-header app-header--nav">
        <div className="app-header__row">
          {current !== undefined && (
            <button
              type="button"
              className="app-header__menu-toggle"
              aria-expanded={navOpen}
              aria-controls="app-sidebar"
              onClick={() => {
                setNavOpenAt(navOpen ? null : location.pathname);
              }}
            >
              <span aria-hidden="true">☰</span> {t('navMenu')}
            </button>
          )}
          <Link to="/" className="app-header__title app-header__home">
            {t('productName')}
          </Link>
          {current !== undefined && <HeaderSearch workspaceId={current.workspace.id} />}
          {items.length > 1 && <WorkspaceSwitcher items={items} currentId={currentId} />}
          {current !== undefined && <NotificationBell workspaceId={current.workspace.id} />}
          <LanguageSwitcher />
          <UserMenu user={user} />
        </div>
      </header>
      <div className={current !== undefined ? 'app-shell' : undefined}>
        {current !== undefined && (
          <aside
            id="app-sidebar"
            className={navOpen ? 'app-sidebar app-sidebar--open' : 'app-sidebar'}
          >
            <WorkspaceNav membership={current} />
          </aside>
        )}
        <main id="main" className="app-main app-main--wide" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </>
  );
}

function WorkspaceSwitcher({
  items,
  currentId,
}: {
  items: readonly Membership[];
  currentId: string | undefined;
}) {
  const navigate = useNavigate();
  return (
    <div className="workspace-switcher">
      <label htmlFor="workspace-switcher">{phrase('Workspace')}</label>
      <select
        id="workspace-switcher"
        className="input input--compact"
        value={currentId ?? ''}
        onChange={(event) => {
          if (event.target.value !== '') {
            void navigate(`/w/${event.target.value}`);
          }
        }}
      >
        {currentId === undefined && <option value="">{phrase('Choose a workspace')}</option>}
        {items.map((item) => (
          <option key={item.workspace.id} value={item.workspace.id}>
            {item.workspace.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function WorkspaceNav({ membership }: { membership: Membership }) {
  const { t } = useI18n();
  const base = `/w/${membership.workspace.id}`;
  return (
    <nav aria-label={t('navWorkspace')} className="app-nav">
      <ul>
        <li>
          <NavLink to={base} end>
            {t('navHome')}
          </NavLink>
        </li>
        {can(membership, 'topics.read') && (
          <li>
            <NavLink to={`${base}/topics`}>{t('navTopics')}</NavLink>
          </li>
        )}
        {can(membership, 'decisions.read') && (
          <li>
            <NavLink to={`${base}/decisions`}>{t('navDecisions')}</NavLink>
          </li>
        )}
        {can(membership, 'tasks.read') && (
          <li>
            <NavLink to={`${base}/tasks`}>{t('navTasks')}</NavLink>
          </li>
        )}
        {can(membership, 'updates.read') && (
          <li>
            <NavLink to={`${base}/updates`}>{t('navUpdates')}</NavLink>
          </li>
        )}
        {can(membership, 'meetings.read') && (
          <li>
            <NavLink to={`${base}/meetings`}>{t('navMeetings')}</NavLink>
          </li>
        )}
        {can(membership, 'goals.read') && (
          <li>
            <NavLink to={`${base}/goals`}>{t('navGoals')}</NavLink>
          </li>
        )}
        <li>
          <NavLink to={`${base}/search`}>{t('navSearch')}</NavLink>
        </li>
        {can(membership, 'reports.read') && (
          <li>
            <NavLink to={`${base}/reports`}>{t('navReports')}</NavLink>
          </li>
        )}
        {can(membership, 'reports.read') && (
          <li>
            <NavLink to={`${base}/execution`}>{t('navExecution')}</NavLink>
          </li>
        )}
        {can(membership, 'founders.read') && (
          <li>
            <NavLink to={`${base}/founders`}>{t('navFounders')}</NavLink>
          </li>
        )}
        {can(membership, 'finance.read') && (
          <li>
            <NavLink to={`${base}/finance`}>{t('navFinance')}</NavLink>
          </li>
        )}
        {hasAdminAccess(membership) && (
          <li>
            <NavLink to={`${base}/admin`}>{t('navAdmin')}</NavLink>
          </li>
        )}
      </ul>
    </nav>
  );
}

function UserMenu({ user }: { user: CurrentUser }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const logout = useLogout();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const close = () => {
    if (detailsRef.current !== null) {
      detailsRef.current.open = false;
    }
  };

  // A native <details> stays open until its summary is clicked again; menus should not.
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const details = detailsRef.current;
      if (
        details?.open === true &&
        event.target instanceof Node &&
        !details.contains(event.target)
      ) {
        details.open = false;
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const details = detailsRef.current;
      if (event.key === 'Escape' && details?.open === true) {
        details.open = false;
        details.querySelector('summary')?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return (
    <details className="user-menu" ref={detailsRef}>
      <summary>
        <span className="visually-hidden">{t('accountMenu')}</span> {user.displayName}
      </summary>
      <ul className="user-menu__list">
        <li>
          <Link to="/profile" onClick={close}>
            {t('profile')}
          </Link>
        </li>
        <li>
          <Link to="/security" onClick={close}>
            {t('security')}
          </Link>
        </li>
        <li>
          <button
            type="button"
            className="link-button"
            disabled={logout.isPending}
            onClick={() => {
              logout.mutate(undefined, {
                onSuccess: () => void navigate('/login', { replace: true }),
              });
            }}
          >
            {logout.isPending ? t('signingOut') : t('signOut')}
          </button>
        </li>
      </ul>
      {logout.isError && <ErrorNotice error={logout.error} />}
    </details>
  );
}
